import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, TrendingUp, Globe, Trophy, Eye, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const fmtCurrency = (amount, currency = "USD") =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
};

/**
 * ValidatedQuotes — tableau de bord des devis « Validés par le client ».
 *
 * KPIs en haut (nombre, montant total, top pays, top partenaire) puis un
 * tableau filtrable listant chaque devis remporté avec un lien vers la fiche
 * détaillée dans /quotes.
 */
export default function ValidatedQuotes() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = () => {
    setLoading(true);
    api
      .get("/quotes/validated/summary")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Échec du chargement"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const items = (data?.items || []).filter((it) => {
    if (!filter) return true;
    const haystack = [
      it.title, it.customer_name, it.customer_info?.country,
      it.customer_info?.partner_name, it.client_validated_by_email, it.use_case,
    ].join(" ").toLowerCase();
    return haystack.includes(filter.toLowerCase());
  });

  return (
    <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-12" data-testid="validated-quotes-page">
      <div className="flex items-start justify-between gap-4 mb-6 sm:mb-8 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 flex items-center gap-2">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            Devis validés par le client
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Suivi des affaires remportées : devis cochés « Validé par le client » dans
            <Link to="/quotes" className="text-[#D42B1E] hover:underline ml-1">Mes devis</Link>.
            {data?.is_privileged && <span className="ml-2 text-[10px] font-mono uppercase tracking-wide bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded-sm border border-amber-300">Vue globale Reviewer/Admin</span>}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          className="rounded-sm border-zinc-300"
          data-testid="validated-refresh-btn"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Actualiser
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard
          label="Devis validés"
          value={loading ? "…" : (data?.count ?? 0)}
          icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />}
          accent="emerald"
          testid="kpi-count"
        />
        <KpiCard
          label="Montant total"
          value={loading ? "…" : Object.entries(data?.by_currency || {}).map(([c, a]) => fmtCurrency(a, c)).join(" + ") || "—"}
          icon={<TrendingUp className="h-4 w-4 text-[#D42B1E]" />}
          accent="rouge"
          testid="kpi-total"
        />
        <KpiCard
          label="Top pays"
          value={loading ? "…" : (data?.by_country?.[0]?.country || "—")}
          subline={loading ? "" : data?.by_country?.[0] ? `${data.by_country[0].count} devis · ${fmtCurrency(data.by_country[0].amount)}` : ""}
          icon={<Globe className="h-4 w-4 text-zinc-700" />}
          testid="kpi-country"
        />
        <KpiCard
          label="Top Sales"
          value={loading ? "…" : (data?.by_sales?.[0]?.sales || "—")}
          subline={loading ? "" : data?.by_sales?.[0] ? `${data.by_sales[0].count} devis · ${fmtCurrency(data.by_sales[0].amount)}` : ""}
          icon={<Trophy className="h-4 w-4 text-amber-600" />}
          testid="kpi-sales"
        />
      </div>

      {/* Recap table */}
      <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-3 flex-wrap">
          <h2 className="text-sm font-semibold tracking-[0.15em] uppercase text-zinc-500">
            Récapitulatif détaillé
          </h2>
          <span className="text-[11px] text-zinc-400 font-mono">{items.length} ligne{items.length !== 1 ? "s" : ""}</span>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtrer (client, pays, partenaire, use case…)"
            className="ml-auto text-xs border border-zinc-300 rounded-sm px-3 py-1.5 w-full sm:w-72 focus:outline-none focus:border-[#D42B1E]"
            data-testid="validated-filter-input"
          />
        </div>
        {loading ? (
          <div className="p-10 text-sm text-zinc-500 text-center">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle2 className="h-8 w-8 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm text-zinc-600 font-medium">
              {filter ? "Aucun devis ne correspond au filtre." : "Aucun devis validé pour l'instant."}
            </p>
            {!filter && (
              <p className="text-xs text-zinc-400 mt-2">
                Cochez « Validé par le client » sur un devis dans
                <Link to="/quotes" className="text-[#D42B1E] hover:underline ml-1">Mes devis</Link>
                {" "}pour le retrouver ici.
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="validated-quotes-table">
              <thead className="bg-zinc-50 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-3">Validé le</th>
                  <th className="text-left px-4 py-3">Devis</th>
                  <th className="text-left px-4 py-3">Client</th>
                  <th className="text-left px-4 py-3">Pays</th>
                  <th className="text-left px-4 py-3">Partenaire</th>
                  <th className="text-left px-4 py-3">Use case</th>
                  <th className="text-right px-4 py-3">Util.</th>
                  <th className="text-right px-4 py-3">Sites</th>
                  <th className="text-right px-4 py-3">Montant</th>
                  <th className="text-center px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {items.map((it) => (
                  <tr
                    key={it.id}
                    className="hover:bg-zinc-50/50 transition-colors"
                    data-testid={`validated-row-${it.id}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-zinc-600 whitespace-nowrap">
                      {fmtDate(it.client_validated_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-zinc-900 truncate max-w-[260px]" title={it.title}>
                        {it.title}
                      </div>
                      {it.client_validated_note && (
                        <div className="text-[11px] text-zinc-500 italic truncate max-w-[260px]" title={it.client_validated_note}>
                          « {it.client_validated_note} »
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{it.customer_name || "—"}</td>
                    <td className="px-4 py-3 text-zinc-700">{it.customer_info?.country || "—"}</td>
                    <td className="px-4 py-3 text-zinc-700">{it.customer_info?.partner_name || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-700">
                        {it.use_case || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-zinc-700">{it.users || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-zinc-700">{it.sites || "—"}</td>
                    <td className="px-4 py-3 text-right font-semibold text-zinc-900 whitespace-nowrap">
                      {fmtCurrency(it.total_cost, it.currency)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Link
                        to={`/quotes`}
                        className="inline-flex items-center gap-1 text-xs text-[#D42B1E] hover:underline"
                        data-testid={`validated-view-${it.id}`}
                      >
                        <Eye className="h-3 w-3" /> Voir
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-zinc-50">
                <tr>
                  <td colSpan={8} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-zinc-600">
                    Total cumulé
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-zinc-900" data-testid="validated-grand-total">
                    {Object.entries(data?.by_currency || {}).map(([c, a]) => fmtCurrency(a, c)).join(" + ") || "—"}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Per-country & per-sales breakdowns */}
      {!loading && (data?.count || 0) > 0 && (
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <BreakdownCard
            title="Répartition par pays"
            rows={data?.by_country || []}
            keyName="country"
            testid="breakdown-country"
          />
          <SalesRankingCard
            rows={data?.by_sales || []}
            testid="breakdown-sales"
          />
        </div>
      )}
    </div>
  );
}

function SalesRankingCard({ rows, testid }) {
  if (!rows || rows.length === 0) return null;
  // Already sorted by count desc on the backend — render with a rank column
  // so the dashboard reads as a leaderboard from 1st to last.
  const medal = (rank) => {
    if (rank === 1) return "bg-amber-400 text-amber-950";
    if (rank === 2) return "bg-zinc-300 text-zinc-800";
    if (rank === 3) return "bg-orange-300 text-orange-900";
    return "bg-zinc-100 text-zinc-600";
  };
  return (
    <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2">
        <Trophy className="h-3.5 w-3.5 text-amber-600" />
        <h3 className="text-sm font-semibold tracking-[0.15em] uppercase text-zinc-500">
          Classement Sales
        </h3>
        <span className="text-[11px] text-zinc-400 font-mono ml-auto">{rows.length} commercial{rows.length > 1 ? "aux" : ""}</span>
      </div>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-zinc-100">
          {rows.map((r, idx) => {
            const rank = idx + 1;
            return (
              <tr key={r.email || r.sales} className="hover:bg-zinc-50/40" data-testid={`sales-rank-${rank}`}>
                <td className="px-3 py-2.5 w-12">
                  <span className={`inline-flex items-center justify-center min-w-[26px] h-[22px] rounded-sm text-[11px] font-mono font-bold ${medal(rank)}`}>
                    {rank}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <div className="font-medium text-zinc-900 truncate max-w-[260px]" title={r.sales}>{r.sales}</div>
                  {r.email && r.email !== r.sales.toLowerCase() && (
                    <div className="text-[10px] font-mono text-zinc-400 truncate max-w-[260px]" title={r.email}>{r.email}</div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-xs text-zinc-500 whitespace-nowrap">{r.count} devis</td>
                <td className="px-5 py-2.5 text-right font-semibold text-zinc-900 whitespace-nowrap">{fmtCurrency(r.amount)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function KpiCard({ label, value, subline, icon, accent, testid }) {
  const accentRing = accent === "emerald" ? "border-emerald-200 bg-emerald-50/40"
    : accent === "rouge" ? "border-[#D42B1E]/20 bg-[#D42B1E]/5"
    : "border-zinc-200 bg-white";
  return (
    <div className={`border rounded-sm p-4 ${accentRing}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">
        {icon} {label}
      </div>
      <div className="mt-2 text-2xl font-semibold text-zinc-900 truncate" title={String(value)}>{value}</div>
      {subline && <div className="text-[11px] text-zinc-500 mt-1 font-mono truncate">{subline}</div>}
    </div>
  );
}

function BreakdownCard({ title, rows, keyName, testid }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3 border-b border-zinc-100">
        <h3 className="text-sm font-semibold tracking-[0.15em] uppercase text-zinc-500">{title}</h3>
      </div>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-zinc-100">
          {rows.map((r) => (
            <tr key={r[keyName]} className="hover:bg-zinc-50/40">
              <td className="px-5 py-2.5 font-medium text-zinc-800">{r[keyName]}</td>
              <td className="px-5 py-2.5 text-right font-mono text-xs text-zinc-500">{r.count} devis</td>
              <td className="px-5 py-2.5 text-right font-semibold text-zinc-900">{fmtCurrency(r.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
